import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardHeader,
    CardContent,
    Stack,
    Typography,
    CircularProgress,
    Alert,
    Divider,
    Button,
    Paper,
    Tooltip,
    IconButton,
    Snackbar,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useNavigate, useParams } from "react-router-dom";
import { getUserAnalyzerResult, runUserAnalyzer } from "../api/AiAgentApi";

export const UserAnalyzerResultPage = ({ userId: userIdProp }) => {
    const { userId: userIdFromParams } = useParams();
    const userId = userIdProp ?? userIdFromParams;
    const navigate = useNavigate();

    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const [running, setRunning] = useState(false);
    const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

    const fetchData = async () => {
        if (!userId) {
            setErr("Не указан userId.");
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            setErr(null);
            const res = await getUserAnalyzerResult(userId);
            const sorted = [...res].sort(
                (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
            );
            setList(sorted);
        } catch (e) {
            setErr(e?.message || "Не удалось загрузить данные");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const handleCopy = async (text) => {
        try {
            await navigator.clipboard.writeText(text || "");
        } catch {
            // no-op
        }
    };

    const handleDownload = (dataItem, prettyResult, isJson) => {
        const blob = new Blob(
            [prettyResult || ""],
            { type: isJson ? "application/json;charset=utf-8" : "text/plain;charset=utf-8" }
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const ts = dataItem.createdAt
            ? new Date(dataItem.createdAt).toISOString().replace(/[:.]/g, "-")
            : "now";
        a.href = url;
        a.download = isJson
            ? `user-${dataItem.userId}-analyzer-${ts}.json`
            : `user-${dataItem.userId}-analyzer-${ts}.txt`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const handleRunAnalyzer = async () => {
        if (!userId) return;
        try {
            setRunning(true);
            const newResult = await runUserAnalyzer(userId);
            setSnack({ open: true, msg: "Анализ выполнен", severity: "success" });
            setList((prev) => [newResult, ...prev]);
        } catch (e) {
            setSnack({
                open: true,
                msg: e?.message || "Не удалось запустить анализ",
                severity: "error",
            });
        } finally {
            setRunning(false);
        }
    };


    return (
        <Box sx={{ p: 2, maxWidth: 1100, mx: "auto" }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Button
                    variant="text"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                >
                    Назад
                </Button>

                <Box sx={{ flex: 1 }} />

                {/* Кнопка запуска анализатора */}
                <Tooltip title="Запустить анализ для пользователя">
          <span>
            <Button
                variant="contained"
                color="success"
                startIcon={running ? <CircularProgress size={18} /> : <PlayArrowIcon />}
                onClick={handleRunAnalyzer}
                disabled={running || !userId}
                sx={{ mr: 1 }}
            >
              Запустить анализ
            </Button>
          </span>
                </Tooltip>

                <Tooltip title="Обновить">
          <span>
            <IconButton onClick={fetchData} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </span>
                </Tooltip>
            </Stack>

            {err && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {err}
                </Alert>
            )}

            {loading && (
                <Stack alignItems="center" sx={{ py: 6 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Загрузка…
                    </Typography>
                </Stack>
            )}

            {!loading && !err && list.length === 0 && (
                <Alert severity="info">Нет данных для отображения.</Alert>
            )}

            {!loading && !err && list.length > 0 && (
                <Stack spacing={3}>
                    {list.map((item) => {
                        let prettyResult = "";
                        let isJson = false;
                        if (item.result) {
                            try {
                                const obj = JSON.parse(item.result);
                                prettyResult = JSON.stringify(obj, null, 2);
                                isJson = true;
                            } catch {
                                prettyResult = item.result;
                            }
                        }

                        const createdAtLocal = item.createdAt
                            ? new Date(item.createdAt).toLocaleString()
                            : "—";

                        return (
                            <Card key={item.id} variant="outlined">
                                <CardHeader
                                    title={`Анализ №${item.id}`}
                                    subheader={`Создано: ${createdAtLocal}`}
                                    action={
                                        <Stack direction="row" spacing={1}>
                                            <Tooltip title="Скопировать">
                        <span>
                          <IconButton
                              onClick={() => handleCopy(prettyResult)}
                              disabled={!prettyResult}
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </span>
                                            </Tooltip>
                                            <Tooltip title="Скачать">
                        <span>
                          <IconButton
                              onClick={() =>
                                  handleDownload(item, prettyResult, isJson)
                              }
                              disabled={!prettyResult}
                          >
                            <FileDownloadIcon />
                          </IconButton>
                        </span>
                                            </Tooltip>
                                        </Stack>
                                    }
                                />
                                <Divider />
                                <CardContent>
                                    <Typography variant="overline" color="text.secondary">
                                        Результат {isJson ? "(JSON)" : "(Текст)"}
                                    </Typography>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            mt: 1,
                                            p: 2,
                                            bgcolor: "background.paper",
                                            maxHeight: 520,
                                            overflow: "auto",
                                            fontFamily:
                                                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                            fontSize: 13,
                                            lineHeight: 1.5,
                                        }}
                                    >
                    <pre
                        style={{
                            margin: 0,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                        }}
                    >
                      {prettyResult}
                    </pre>
                                    </Paper>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Stack>
            )}

            <Snackbar
                open={snack.open}
                autoHideDuration={3500}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                message={snack.msg}
            />
        </Box>
    );
};

export default UserAnalyzerResultPage;
